<template>
  <div>
    <h3>Recently Viewed</h3>
    <div class="button-container">
      <button
        v-for="option in contentOptions"
        :key="option.buttonText"
        :class="['btn btn-lg', { 'selected-btn': selectedOption.value === option.value }]"
        :aria-pressed="selectedOption.value === option.value"
        :aria-label="option.buttonText"
        @click="selectOption(option)">
        {{ option.buttonText }}</button>
    </div>
    <p class="loading-notice text-muted" v-if="loading"><i class="fa fa-circle-o-notch fa-spin"/>&nbsp;Loading {{ selectedOption.value }}...</p>

    <div v-if="!loading && error" class="error-container">
      <span class="material-icons-outlined">info</span>
      We're not able to retrieve {{ selectedOption.value }} right now. Try again later.
    </div>

    <div v-if="!loading && !error && content.length">
      <div class="goals-container" v-if="selectedOption.value === 'goals'">
        <GoalCardContainer
          v-for="(goal, index) in content"
          :key="goal.uuid"
          :position="index"
          :goal="goal"
          :restriction-type="goal.restrictionType"
          :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(goal.primary_subject))"
          :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(goal.primary_subject))" />
      </div>
      <div v-if="selectedOption.value === 'strategies'" class="strategies-container">
        <div v-for="strategy in content" :key="strategy.uuid">
          <LibraryStrategyCard
            :removable="false"
            :strategy="strategy"
            :content-uuid="strategy.uuid"
            :saved="false"
            :show-resource-count="true"
            :show-timestamp="false"/>
        </div>
      </div>
      <div v-if="selectedOption.value === 'resources'">
        <ResourcePreviewCard
          v-for="resource in content"
          :key="resource.uuid"
          v-bind="{...resource, num_pages: 0}"
          :show-resource-type="false"
          :omit-description="true"
          :screenshots="resource.screenshots"
          :new-homepage="true"
          :heading-level="4" />
      </div>
      <a
        v-if="content.length"
        :href="getUrl()"
        @click="handleViewMoreClick">
        View more {{ selectedOption.value }}
      </a>
    </div>
    <div v-if="!loading && !error && !content.length" class="empty-state">
      <p>You haven't viewed any {{ selectedOption.value }} yet</p>
      <a
        class="browse-link"
        href="#"
        @click.prevent="handleBrowseClick">
        Browse {{ selectedOption.browseText }}
      </a>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex'
  import { noSelMixin } from '@/contentprofile/no_sel_mixins'
  import ResourcePreviewCard from '@/components/ResourcePreviewCard'
  import GoalCardContainer from '@/components/search/goalcard/GoalCardContainer'
  import LibraryStrategyCard from '@/components/library/LibraryStrategyCard'
  import { toolkitUrls } from '@/links'
  import { getRecentlyViewedContent } from '@/api/recentlyviewed'

  const CONTENT_OPTIONS = [
    {
      buttonText: 'Goals',
      value: 'goals',
      apiType: 'goal',
      limit: 4,
      browsePage: 'BrowseGoalsPage',
      browseText: 'goals'
    },
    {
      buttonText: 'Strategies',
      value: 'strategies',
      apiType: 'strategy',
      limit: 3,
      browsePage: 'BrowseStrategiesPage',
      browseText: 'strategies'
    },
    {
      buttonText: 'Resources',
      value: 'resources',
      apiType: 'resource',
      limit: 3,
      browsePage: 'BrowseAssessments',
      browseText: 'assessments'
    }
  ];

  export default {
    name: 'HomePageRecentlyViewed',
    components: {
      ResourcePreviewCard,
      GoalCardContainer,
      LibraryStrategyCard
    },
    mixins: [noSelMixin],
    data() {
      return {
        ...toolkitUrls,
        loading: true,
        content: [],
        error: false,
        selectedOption: CONTENT_OPTIONS[0],
        contentOptions: CONTENT_OPTIONS
      }
    },
    computed: {
      ...mapGetters({
        dropdownStandardSetForSubject: 'contentProfile/dropdownStandardSetForSubject',
        standardSetLabelForSubjectCard: 'contentProfile/standardSetLabelForSubjectCard',
        userId: 'user/userId',
        isFetching: 'user/isFetching',
        betaVisibility: 'visibility/betaVisibility'
      })
    },
    methods: {
      ...mapMutations('user', ['pushOnetimeCallback']),
      selectOption(option) {
        this.selectedOption = option;
        this.$sendEvent(`Selected Recently Viewed ${option.buttonText}`);
      },
      getUrl() {
        if (this.content.length) {
          const betaParam = this.betaVisibility ? '?beta=true' : '';
          return `${this.home}/v/library/recently_viewed/${this.selectedOption.value}${betaParam}`;
        } else {
          return this.selectedOption.browsePage;
        }
      },
      handleViewMoreClick() {
        this.$sendEvent(`Clicked View More Recently Viewed ${this.selectedOption.buttonText}`);
      },
      handleBrowseClick() {
        const capitalizedBrowseText = this.selectedOption.browseText.charAt(0).toUpperCase() + this.selectedOption.browseText.slice(1)
        this.$sendEvent(`Clicked Browse ${capitalizedBrowseText}`);
        this.$router.push({ name: this.selectedOption.browsePage });
      },
      transformGoal(contentBody) {
        const goal = contentBody
        goal.goal_text = goal.plain_goal_text
        goal.primary_subject = goal.subject
        goal.has_progress_monitoring = goal.goal_resources.length > 0

        if (goal.access_point_level) {
          goal.level = goal.access_point_level
          goal.levels = ['grade_level', 'approaching', 'developing']
        }

        return goal
      },
      transformContent(content) {
        if (this.selectedOption.apiType === 'goal') {
          return content.map(item => this.transformGoal(item.content_body))
        }
        return content.map(item => item.content_body)
      },
      async fetchContent() {
        this.loading = true
        this.content = []
        this.error = false

        try {
          const response = await getRecentlyViewedContent(
            this.userId,
            this.selectedOption.apiType,
            this.selectedOption.limit
          )
          this.content = this.transformContent(response.content || [])
        } catch (err) {
          console.error(`Error fetching recently viewed ${this.selectedOption.value}:`, err)
          this.error = true
        } finally {
          this.loading = false
        }
      }
    },
    watch: {
      selectedOption: {
        immediate: true,
        handler: 'fetchContent'
      }
    },
    async created () {
      if (!this.isFetching && this.userId) {
        await this.fetchContent()
      } else {
        this.pushOnetimeCallback(async () => {
          if (this.userId) {
            await this.fetchContent()
          }
        })
      }
    },
    eventContext() {
      return {
        ref: 'home',
      }
    }
  };
</script>

<style lang="less" scoped>
.button-container {
  display: flex;
  gap: 20px;
  margin: var(--spacing-lg) 0 var(--spacing-xl) 0;
}

.goals-container {
  display: grid;
  grid-template-columns: repeat(2, 277px);
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
}

.strategies-container {
  display: grid;
  grid-template-columns: repeat(1, 420px);
}

.btn {
  text-align: center;
  background-color: white;
  padding: var(--spacing-md) var(--spacing-xl);
  color: var(--blue-default);
  font-size: var(--text-md);
  border-radius: var(--radius-base);

  &:hover {
    background-color: var(--gray-border-weak);
    color: var(--blue-default);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    background-color: var(--gray-border-weak);
    color: var(--blue-default);
    outline: 2px solid var(--gray-weak);
    outline-offset: var(--spacing-xxs);
  }
}

.selected-btn {
  text-align: center;
  background-color: var(--blue-default);
  padding: var(--spacing-md) var(--spacing-xl);
  color: white;
  font-size: var(--text-md);

  &:hover {
    background-color: var(--blue-default);
    color: white;
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    background-color: var(--blue-default);
    color: white;
    outline: 2px solid var(--blue-default);
    outline-offset: var(--spacing-xxs);
  }
}

.empty-state {
  font-size: var(--text-xl);
  margin-top: var(--spacing-3xl) 0 var(--spacing-xs) 0;
  color: var(--gray-default);
  font-style: italic;
}

.error-container {
  display: flex;
  align-items: center;
  width: fit-content;
  border-radius: var(--radius-base);
  padding: var(--spacing-md);
  background-color: var(--blue-weak);
  color: var(--gray-default);
}

.material-icons-outlined {
  color: var(--blue-default);
  margin-right: var(--spacing-md);
}

.browse-link {
  color: var(--blue-default);
  font-weight: var(--font-medium);
  font-style: normal;
}
</style>